import { api, ErroApi } from './client'

export type EspecificacaoDeLivro = {
  tiragem: number
  paginas: number
  formato: string
  papelDoMiolo: string
  comOrelhas: boolean
  capaFrenteEVerso: boolean
  laminacao: string | null
  shrink: boolean
  outraBrochura: boolean
}

export type LinhaDaMemoria = {
  etapa: string
  descricao: string
  valor: number
  origem: string | null
}

export type OrcamentoCalculado = {
  sucesso: boolean
  numero: string | null
  total: number
  unitario: number
  memoria: LinhaDaMemoria[]
  versaoDaTabela: number
  erro: string | null

  /** Regra de exceção do cliente: vai para humano. Não é erro. */
  escalonar: string
}

export type Comparacao = OrcamentoCalculado & {
  valorPraticado: number
  diferenca: number
  percentual: number
}

export type VersaoDeTabela = {
  id: number

  /** Sequencial **dentro do formato**: a grade v1 convive com o offset v1. */
  numero: number
  formato: FormatoDePlanilha
  carregadaEm: string
  vigenteDe: string
  carregadaPor: string
  observacao: string | null

  /** Nome do arquivo enviado, para conferência humana. */
  nomeDoArquivo: string | null
  impressao: string | null
  orcamentos: number
}

export type OrcamentoNaLista = {
  numero: string

  /**
   * A referência da PLANILHA, ao lado do número interno (demanda 292). O painel
   * embutido no Chatwoot mostra só ela; esta lista é a ponte entre os dois.
   * `null` quando o orçamento não tem referência (livro, por exemplo).
   */
  referencia: string | null
  familia: string
  tiragem: number
  total: number
  unitario: number
  calculadoEm: string
  versaoDaTabela: number
  origem: string
}

export type DetalheDoOrcamento = {
  numero: string
  familia: string
  tiragem: number
  total: number
  unitario: number
  calculadoEm: string
  calculadoPor: string | null
  versao: number
  entrada: string
  memoria: string
  excluido: boolean

  /** Falso aqui significa que alguma coisa mudou que não deveria ter mudado. */
  reproduzOValorOriginal: boolean

  /** Nulo quando ninguém escolheu frete — "não tem" ≠ "não carregou". */
  frete: FreteEscolhido | null
}

export type OpcaoDeFrete = {
  transportadora: string
  valor: number
  prazoEmDias: number | null
  servico: string | null
  estimativa: boolean
}

export type CotacaoDeFrete = {
  /**
   * 🔴 **O instante em que o SERVIDOR cotou.** A tela o guarda e o devolve ao
   * escolher: é ele que fica gravado, não o momento do clique.
   */
  cotadoEm: string

  opcoes: OpcaoDeFrete[]

  /** Sobe fora das opções para a tela avisar UMA vez, acima da lista. */
  estimativa: boolean

  /** Quem estava habilitado e não devolveu cotação. Frete parcial serve. */
  naoResponderam: string[]

  /** Preenchido quando não há **nenhuma** opção — é diferente de lista vazia. */
  motivo: string | null
}

export type FreteEscolhido = {
  transportadora: string
  servico: string | null
  valor: number
  prazoEmDias: number | null
  cotadoEm: string
  estimativa: boolean
  cepDeDestino: string
  escolhidoPor: string
  escolhidoEm: string
}

export type PedidoDeFrete = {
  cepDeDestino: string
  tiragem: number
  familia?: string | null
  gramatura?: number | null
  largura?: number | null
  altura?: number | null
  paginas?: number
  valorDaMercadoria?: number
}

/** Qual dos três motores cotou. Vai na resposta ao cliente. */
export type MotorDePreco = 'Grade' | 'LivroDigital' | 'LivroOffset' | 'Humano'

export type PedidoDeCotacao = {
  descricao: string
  tiragem: number
  familia?: string | null
  largura?: number | null
  altura?: number | null
  papel?: string | null
  gramatura?: number | null
  cores?: string | null
  colorido?: boolean
  capaDura?: boolean
  ehCaixa?: boolean
  paginas?: number

  /** O formato comercial do livro — "14x21 cm". NÃO é a família. */
  formato?: string | null
  comOrelha?: boolean
  comShrink?: boolean
  laminacao?: 'Nenhuma' | 'FoscaOuBrilho' | 'SoftTouch'
}

export type Cotacao = {
  cotou: boolean
  motor: MotorDePreco

  /** "livro colorido, 500 exemplares → offset". Sem isso ninguém explica o preço. */
  porque: string
  unitario: number
  total: number
  memoria: LinhaDaMemoria[]

  /** O código do orçamento-base no GE — exigência do cliente, ata de 24/06. */
  codigo: number | null
  familia: string | null

  /** O que dizer ao cliente. Nunca uma negativa. */
  explicacao: string | null
  transbordo: string | null
  versaoDaTabela: number
}

/** O que a barra de filtros escolheu. Datas são DIAS (`aaaa-mm-dd`); `ate` é inclusivo. */
export type FiltroDeOrcamentos = {
  de?: string
  ate?: string
  familia?: string
  assistenteId?: number
  busca?: string
}

/**
 * Uma linha do administrador de orçamentos.
 *
 * 🔴 **Os dois links vêm prontos do servidor.** O endereço da plataforma de
 * atendimento é configuração de lá; a tela não o monta.
 */
export type OrcamentoAdministrado = OrcamentoNaLista & {
  /** A descrição do que foi orçado, encurtada. `null` quando o orçamento não guarda uma. */
  resumo: string | null
  contato: string | null
  canal: string | null
  assistente: string | null

  /** O número da conversa que a equipe vê no atendimento. */
  conversa: number | null
  linkDaConversa: string | null

  /** `null` quando o portal não sabe, com certeza, qual é a ficha do contato. */
  linkDoContato: string | null
  excluido: boolean

  /** Há outro orçamento igual (produto, tiragem e total) na mesma conversa. */
  repetido: boolean

  /**
   * Há contato, mas sem nome que se mostre (o campo está vazio ou guarda um telefone ou
   * e-mail): `contato` vem `null` e a tela diz "sem nome cadastrado".
   *
   * Opcional: o servidor anterior a este campo não o manda, e aí vale o traço.
   */
  contatoSemNome?: boolean
}

export type PaginaDoAdministrador = {
  /** Linhas que o filtro alcança, com as excluídas — é o que pagina. */
  total: number
  pagina: number
  tamanho: number

  /** Orçamentos do filtro, sem os excluídos. */
  quantidade: number

  /** Soma dos totais do filtro, sem os excluídos. */
  soma: number
  tetoDaExportacao: number
  familias: string[]
  assistentes: { id: number; nome: string }[]
  linhas: OrcamentoAdministrado[]
}

function consultaDoFiltro(filtro: FiltroDeOrcamentos) {
  const busca = new URLSearchParams()

  if (filtro.de) busca.set('de', filtro.de)
  if (filtro.ate) busca.set('ate', filtro.ate)
  if (filtro.familia) busca.set('familia', filtro.familia)
  if (filtro.assistenteId) busca.set('assistenteId', String(filtro.assistenteId))
  if (filtro.busca?.trim()) busca.set('busca', filtro.busca.trim())

  return busca
}

export const orcamentos = {
  /**
   * Cota pelo motor certo — qualquer produto, não só livro em offset.
   *
   * Transbordo vem como resposta de sucesso: "vai para uma pessoa" é resultado
   * legítimo da cotação, e tratá-lo como erro mostraria falha para uma regra do
   * cliente funcionando.
   */
  cotar: (marca: string, pedido: PedidoDeCotacao) =>
    api.post<Cotacao>(`/marcas/${marca}/orcamentos/cotar`, pedido),

  cotarFrete: (marca: string, pedido: PedidoDeFrete) =>
    api.post<CotacaoDeFrete>(`/marcas/${marca}/orcamentos/frete`, pedido),

  /**
   * 🔴 `cotadoEm` vai **como veio da cotação**. Trocá-lo pelo relógio do
   * navegador aqui seria gravar a hora do clique — que é exatamente o defeito
   * que o critério 2 da 053.2 existe para impedir.
   */
  escolherFrete: (
    marca: string,
    numero: string,
    escolha: {
      transportadora: string
      valor: number
      cotadoEm: string
      cepDeDestino: string
      prazoEmDias?: number | null
      servico?: string | null
      estimativa?: boolean
    },
  ) =>
    api.post<FreteEscolhido>(
      `/marcas/${marca}/orcamentos/${numero}/frete-escolhido`,
      escolha,
    ),

  simular: (marca: string, especificacao: EspecificacaoDeLivro, versaoDeTabelaId: number | null) =>
    api.post<OrcamentoCalculado>(`/marcas/${marca}/orcamentos/simular`, {
      especificacao,
      versaoDeTabelaId,
    }),

  comparar: (
    marca: string,
    especificacao: EspecificacaoDeLivro,
    versaoDeTabelaId: number | null,
    valorPraticado: number,
  ) =>
    api.post<Comparacao>(`/marcas/${marca}/orcamentos/comparar`, {
      especificacao,
      versaoDeTabelaId,
      valorPraticado,
    }),

  /** A lista com período, produto, assistente, busca, totais e os links da conversa e do contato. */
  administrar: (marca: string, filtro: FiltroDeOrcamentos, pagina = 1, tamanho = 50) => {
    const busca = consultaDoFiltro(filtro)

    busca.set('pagina', String(pagina))
    busca.set('tamanho', String(tamanho))

    return api.get<PaginaDoAdministrador>(
      `/marcas/${encodeURIComponent(marca)}/orcamentos/administrador?${busca}`,
    )
  },

  /**
   * A planilha (CSV) do mesmo filtro.
   *
   * 🔴 **Por `fetch`, e não por link.** `/marcas/:marca/orcamentos/...` é também
   * endereço de tela: um `<a href>` abriria o portal em vez de baixar o arquivo.
   * E o `fetch` leva a sessão do mesmo jeito que todas as outras chamadas.
   */
  exportar: async (marca: string, filtro: FiltroDeOrcamentos) => {
    const base = import.meta.env.VITE_API_URL ?? ''
    const resposta = await fetch(
      `${base}/marcas/${encodeURIComponent(marca)}/orcamentos/administrador/exportar?${consultaDoFiltro(filtro)}`,
      { credentials: 'include', headers: { Accept: 'text/csv' } },
    )

    if (!resposta.ok) throw new ErroApi(resposta.status, null)

    // O nome que o servidor manda só é legível quando a API está na mesma origem
    // (entre origens o navegador esconde o cabeçalho). Por isso há um nome de reserva.
    const doServidor = /filename="?([^";]+)"?/.exec(resposta.headers.get('Content-Disposition') ?? '')?.[1]
    const periodo = [filtro.de, filtro.ate].filter(Boolean).join('-a-')

    return {
      arquivo: await resposta.blob(),
      nome: doServidor ?? `orcamentos-${marca}${periodo ? `-${periodo}` : ''}.csv`,
    }
  },

  detalhar: (marca: string, numero: string) =>
    api.get<DetalheDoOrcamento>(`/marcas/${marca}/orcamentos/${numero}`),

  versoes: () => api.get<VersaoDeTabela[]>('/tabela-de-precos/versoes'),

  /** Lê a planilha e mostra o que ela traz — SEM criar versão. */
  conferirPlanilha: (arquivo: File) => {
    const corpo = new FormData()
    corpo.append('planilha', arquivo)

    return api.enviarFormulario<ConferenciaDePlanilha>('/tabela-de-precos/versoes/conferir', corpo)
  },

  carregarPlanilha: (arquivo: File, observacao: string, vigenteDe: string) => {
    const corpo = new FormData()
    corpo.append('planilha', arquivo)
    if (observacao) corpo.append('observacao', observacao)
    if (vigenteDe) corpo.append('vigenteDe', vigenteDe)

    return api.enviarFormulario<CargaDePlanilha>('/tabela-de-precos/versoes/planilha', corpo)
  },
}

/**
 * As três formas de calcular preço — não três arquivos do mesmo tipo.
 *
 * `Offset` é a calculadora de livro colorido; `Grade` é a tabela por família;
 * `Livros` é a fórmula do digital/POD. Cada uma tem a sua linha de versões.
 */
export type FormatoDePlanilha = 'Offset' | 'Grade' | 'Livros'

/**
 * Achado da conferência de conformidade.
 *
 * 🔴 Nem todo achado bloqueia. Alta suave de preço entre degraus é o cliente
 * arredondando à mão — vale registrar e não vale recusar a planilha. Salto de
 * ordem de grandeza é o dedo escorregado, e esse recusa.
 */
export type AchadoDeConformidade = {
  tipo: string

  /** Aba, linha e degrau — quem for corrigir precisa achar a célula. */
  onde: string
  mensagem: string
  bloqueia: boolean
}

export type ResumoDaPlanilha = {
  /** Offset. */
  formatos?: number
  papeisDeMiolo?: number
  papeisDeCapa?: number
  faixasDeProdutividade?: number
  faixasDeQuebra?: number

  /** Grade. */
  familias?: number
  nomesDasFamilias?: string[]
  itens?: number
  variantes?: number
  codigosDeOrcamento?: number
  comTolerancia?: number

  /** Livros. */
  nomesDosFormatos?: string[]
  custoFixo?: number
}

export type ConferenciaDePlanilha = {
  sucesso: boolean
  arquivo: string

  /** Nulo quando nenhuma assinatura casou — aí a recusa diz as três esperadas. */
  formato: FormatoDePlanilha | null

  /** "grade por família", "calculadora de offset"… — o que a tela mostra. */
  formatoLegivel: string | null
  abasEncontradas: string[]
  erros?: string[]
  avisos: string[]
  achados?: AchadoDeConformidade[]
  versaoVigente?: number | null
  resumo?: ResumoDaPlanilha
  mudancas?: {
    campos: { campo: string; de: number; para: number }[]
    formatosNovos: string[]

    /**
     * O achado mais perigoso da comparação: nenhum erro aparece na carga, e o
     * orçamento daquele formato passa a falhar depois, sem ninguém ligar uma
     * coisa à outra.
     */
    formatosSumidos: string[]
  } | null
}

export type CargaDePlanilha = {
  id: number
  numero: number
  vigenteDe: string
  impressao: string
  formato: FormatoDePlanilha
  formatoLegivel: string
  arquivo: string
  avisos: string[]
  resumo?: ResumoDaPlanilha
}
